import { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  updatePaymentRequest,
  clearUpdatePaymentSuccess,
} from "../../redux/action";
import { PrimaryButton } from "../Buttons";
import { ClipboardCopyIcon } from "@heroicons/react/outline";
import CurrencyDisplay from "../Currency/CurrencyDisplay";
import { useCurrency } from "../../hooks/useCurrency";

export const RequestForm = ({ handleClose, data, getPaymentsRequest }) => {
  const dispatch = useDispatch();
  const {
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
    systemCurrency,
    getExchangeRate,
  } = useCurrency();
  const [displayAmount, setDisplayAmount] = useState(""); // Track display amount separately

  const { updatePaymentLoading, updatePaymentSuccess, updatePaymentError } =
    useSelector((state) => {
      const {
        success: { updatePayment },
        errors: { updatePayment: updateError },
      } = state.ajaxStatuses;
      const { updatePaymentLoading } = state.loadingIndicator;
      return {
        updatePaymentLoading,
        updatePaymentSuccess: updatePayment,
        updatePaymentError: updateError,
      };
    });

  const formik = useFormik({
    initialValues: {
      type: "",
      user: "",
      status: "",
      amount: "",
      transactionRef: "",
    },
    onSubmit: (values) => {
      // Convert display amount to USD for API
      const usdAmount = convertDisplayToUSD(Number(displayAmount));

      // Hydrate with original data
      const payload = {
        ...values,
        amount: usdAmount, // Use USD amount for API
        email: data.user.email,
        userName: data.user.userName,
        type: data.type,
        user: data.user._id,
        walletAddress: data.user.walletAddress,
      };
      dispatch(updatePaymentRequest({ formData: payload, id: data._id }));
    },
  });

  useEffect(() => {
    if (updatePaymentError) toast.error(updatePaymentError);
    if (updatePaymentSuccess) {
      toast.success(updatePaymentSuccess);
      handleClose();
      dispatch(getPaymentsRequest());
      dispatch(clearUpdatePaymentSuccess());
    }
  }, [
    updatePaymentError,
    updatePaymentSuccess,
    dispatch,
    handleClose,
    getPaymentsRequest,
  ]);

  useEffect(() => {
    // Convert USD amount from data to display currency for the form
    if (data.amount !== undefined && data.amount !== null && isLoaded) {
      console.log("RequestForm Debug:", {
        dataAmount: data.amount,
        convertedAmount: convertUSDToDisplay(data.amount),
      });

      const displayAmountValue = convertUSDToDisplay(Number(data.amount));
      setDisplayAmount(displayAmountValue.toString());

      formik.setValues({
        ...formik.values,
        status: data.status || "",
        amount: displayAmountValue, // Set display amount in form
        transactionRef: data.transactionRef || "",
      });
    }
  }, [data, convertUSDToDisplay, isLoaded]);

  const copyToClipboard = (value) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="space-y-6">
      {/* User Summary Box */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 flex flex-col gap-1">
        <div className="flex justify-between items-center border-b border-gray-200 pb-2 mb-2">
          <span className="text-xs font-bold text-gray-500 uppercase">
            User
          </span>
          <span className="text-sm font-bold text-gray-900">
            {data.user?.userName || "Unknown"}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-xs font-bold text-gray-500 uppercase">
            Email
          </span>
          <span className="text-sm text-gray-700">{data.user?.email}</span>
        </div>
        <div className="flex justify-between items-center mt-1">
          <span className="text-xs font-bold text-gray-500 uppercase">
            Balance
          </span>
          <span className="text-sm font-bold text-green-600">
            <CurrencyDisplay
              usdAmount={data.user?.balance}
              className="inline"
            />
          </span>
        </div>
      </div>

      {/* Transaction Ref (Copyable) */}
      {data.transactionRef && (
        <div
          onClick={() => copyToClipboard(data.transactionRef)}
          className="group relative border border-gray-200 rounded-xl p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <label className="text-xs font-bold text-gray-400 uppercase">
            Transaction ID
          </label>
          <p className="font-mono text-sm text-gray-800 truncate pr-8">
            {data.transactionRef}
          </p>
          <ClipboardCopyIcon className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 group-hover:text-primary transition-colors" />
        </div>
      )}

      {/* Image Receipt */}
      {data.imgReceiptUrl && (
        <div className="space-y-2">
          <label className="text-xs font-bold text-gray-500 uppercase">
            Proof of Payment
          </label>
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
            <img
              src={data.imgReceiptUrl}
              alt="Receipt"
              className="w-full h-auto object-cover max-h-60 hover:scale-105 transition-transform duration-500 cursor-pointer"
              onClick={() => window.open(data.imgReceiptUrl, "_blank")}
            />
          </div>
          <p className="text-xs text-center text-gray-400">
            Click image to view full size
          </p>
        </div>
      )}

      <form onSubmit={formik.handleSubmit} className="space-y-5">
        {/* Amount Input */}
        <div>
          <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
            Amount ({getCurrencySymbol()})
          </label>
          <input
            type="number"
            value={displayAmount}
            onChange={(e) => {
              const value = e.target.value;
              setDisplayAmount(value);
              formik.setFieldValue("amount", value);
            }}
            placeholder={`Enter amount in ${systemCurrency}`}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none font-bold text-gray-900"
          />
        </div>

        {/* Status Select */}
        <div>
          <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
            Update Status
          </label>
          <div className="relative">
            <select
              {...formik.getFieldProps("status")}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none text-gray-900 appearance-none"
            >
              <option value="" disabled>
                Select Action
              </option>
              <option value="Approved">Approve Request</option>
              <option value="Pending">Mark as Pending</option>
              <option value="Declined">Decline Request</option>
            </select>
            {/* Custom Chevron */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4 text-gray-500">
              <svg className="h-4 w-4 fill-current" viewBox="0 0 20 20">
                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Withdrawal specific fields */}
        {data.type === "Withdrawal" && (
          <div>
            <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
              Transaction Ref (Outgoing)
            </label>
            <input
              type="text"
              {...formik.getFieldProps("transactionRef")}
              placeholder="Enter hash/ref ID"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none text-gray-900"
            />
          </div>
        )}

        <div className="pt-4">
          <PrimaryButton
            text={updatePaymentLoading ? "Processing..." : "Confirm Update"}
            action={formik.handleSubmit}
            loading={updatePaymentLoading}
            className="w-full"
          />
        </div>
      </form>
    </div>
  );
};
